import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Debounced handler para cambios de cantidad
    this.debouncedOnChange = debounce(this.onQuantityChange.bind(this), 300);

    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  async onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const productId = lineItemRow.getAttribute('data-product-id');
    const currentVariantId = lineItemRow.getAttribute('data-variant-id');
    
    if (!productId) {
      console.error('Product ID not found for line', line);
      return;
    }

    try {
      // Mostrar loading visual
      const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer();

      // Llamada a tu API para obtener la variante correcta basada en cantidad
      const response = await fetch('https://melmx.lievant.com.mx/api/shopify/variant-cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: `gid://shopify/Product/${productId}`,
          quantity,
          SHOPIFY_STORE: window.location.hostname
        })
      });

      const data = await response.json();

      if (!data.variantId) {
        console.warn('No variantId returned from rules API');
        // Si no hay regla específica, solo actualizar cantidad
        this.updateQuantity({ line, quantity, action: 'change' });
        return;
      }

      const numericVariantId = this.getVariantIdFromGID(data.variantId);
      const numericCurrentVariantId = currentVariantId;

      // Si la variante es diferente, hacer el cambio
      if (numericVariantId !== numericCurrentVariantId) {
        await this.changeVariant(line, numericVariantId, quantity);
      } else {
        // Si es la misma variante, solo actualizar cantidad
        this.updateQuantity({ line, quantity, action: 'change' });
      }

    } catch (err) {
      console.error('Error fetching variant from rules API:', err);
      // En caso de error, solo actualizar la cantidad sin cambiar variante
      this.updateQuantity({ line, quantity, action: 'change' });
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const payload = {
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    const body = JSON.stringify(payload);

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * Cambia la variante de un producto en el carrito
   * @param {number} line - Número de línea en el carrito
   * @param {string} variantId - ID numérico de la nueva variante
   * @param {number} quantity - Cantidad deseada
   */
  changeVariant = async (line, variantId, quantity) => {
    const cartPerformanceMarker = cartPerformance.createStartingMarker('change-variant:user-action');
    
    try {
      // Deshabilitar la UI mientras procesamos
      this.#disableCartItems();
      
      const { cartTotal } = this.refs;
      cartTotal?.shimmer();

      // Agregar shimmer a la línea específica
      const lineItemRow = this.refs.cartItemRows[line - 1];
      const textComponent = lineItemRow?.querySelector('text-component');
      textComponent?.shimmer();

      // 1. Eliminar la línea antigua
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: 0 })
      });

      // 2. Agregar la nueva variante
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          id: variantId, 
          quantity: quantity 
        })
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add variant to cart');
      }

      // 3. Obtener todas las secciones del carrito que necesitan actualizarse
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sectionsParam = Array.from(sectionsToUpdate).join(',');
      
      // 4. Obtener el HTML actualizado de todas las secciones
      const sectionsResponse = await fetch(
        `${window.location.pathname}?sections=${sectionsParam}`
      );
      const sectionsData = await sectionsResponse.json();

      // 5. Obtener el nuevo conteo de items
      const newSectionHTML = new DOMParser().parseFromString(
        sectionsData[this.sectionId],
        'text/html'
      );
      
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      // 6. Disparar evento de actualización del carrito
      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: sectionsData,
        })
      );

      // 7. Actualizar visualmente cada sección
      sectionsToUpdate.forEach((sectionId) => {
        if (sectionsData[sectionId]) {
          morphSection(sectionId, sectionsData[sectionId]);
        }
      });

      // 8. Limpiar el shimmer
      resetShimmer(this);

    } catch (error) {
      console.error('Error al cambiar variante:', error);
      
      // En caso de error, recargar la sección para mantener consistencia
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      
    } finally {
      // 9. Rehabilitar la UI
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformanceMarker);
    }
  }

  /**
   * Extrae el ID numérico de un GID de Shopify
   * @param {string} gid - GID en formato "gid://shopify/ProductVariant/12345"
   * @returns {string} ID numérico
   */
  getVariantIdFromGID(gid) {
    return gid.split('/').pop();
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}